import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, ErrorState, LoadingState } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  EVENT_LABELS,
  useMonth,
  type CalendarEvent,
} from '@/features/calendar/api';
import { AddEventDrawer } from './AddEventDrawer';
import { CalendarEventDialog } from './CalendarEventDialog';
import { todayKl, shiftMonth } from './time';
import styles from './calendar.module.css';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LEGEND = [
  ['public_holiday', 'Public holiday · closed'],
  ['doctor_leave', 'Doctor leave'],
  ['clinic_closure', 'Closure or short hours'],
  ['ramadan_hours', 'Ramadan hours'],
] as const;

/** "Calendar · month": bookings per day and the days that change opening. */
export function MonthTab() {
  const { hasPermission } = useSession();
  const [month, setMonth] = useState(todayKl().slice(0, 7));
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<CalendarEvent | null>(null);
  const [changing, setChanging] = useState<CalendarEvent | null>(null);
  const canManage = hasPermission('calendar:manage');
  const { data, isLoading, error, refetch } = useMonth(month);
  const title = new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  // Monday-first grid: leading blanks before the 1st.
  const lead = data ? (data.days[0].weekday + 6) % 7 : 0;
  const trail = data ? (7 - ((lead + data.days.length) % 7)) % 7 : 0;
  return (
    <Card
      title={
        <span className={styles.monthHead}>
          <span>{title}</span>
        </span>
      }
      actions={
        <>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Previous month"
            icon={<ChevronLeft size={16} />}
            onClick={() => setMonth(shiftMonth(month, -1))}
          />
          <Button
            size="sm"
            variant="ghost"
            aria-label="Next month"
            icon={<ChevronRight size={16} />}
            onClick={() => setMonth(shiftMonth(month, 1))}
          />
          {canManage && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={14} />}
              onClick={() => setAdding(true)}
            >
              Add
            </Button>
          )}
        </>
      }
      flush
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && (
        <>
          <div
            className={styles.grid}
            role="grid"
            aria-label={`Calendar ${title}`}
          >
            {DOW.map((d) => (
              <div key={d} className={styles.dow} role="columnheader">
                {d.toUpperCase()}
              </div>
            ))}
            {Array.from({ length: lead }, (_, i) => (
              <div key={`b${i}`} className={`${styles.day} ${styles.blank}`} />
            ))}
            {data.days.map((d) => {
              const holiday = d.events.some(
                (e) =>
                  e.type === 'public_holiday' || e.type === 'clinic_closure',
              );
              return (
                <div
                  key={d.date}
                  role="gridcell"
                  aria-label={d.date}
                  className={[
                    styles.day,
                    d.date === data.today && styles.today,
                    holiday && styles.holidayDay,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className={styles.dayNum}>
                    {Number(d.date.slice(8))}
                  </span>
                  {d.events.map((e) => {
                    const full = data.events.find((x) => x.id === e.id);
                    const label = `${EVENT_LABELS[e.type]}: ${e.description}`;
                    // RL-1047: a saved entry opens its details (change/remove).
                    return full ? (
                      <button
                        key={e.id}
                        type="button"
                        className={`${styles.ev} ${styles.evButton} ${styles[e.type]}`}
                        title={label}
                        aria-label={label}
                        aria-haspopup="dialog"
                        onClick={() => setViewing(full)}
                      >
                        {e.description}
                      </button>
                    ) : (
                      <span
                        key={e.id}
                        className={`${styles.ev} ${styles[e.type]}`}
                        title={label}
                      >
                        {e.description}
                      </span>
                    );
                  })}
                  {d.bookings > 0 && (
                    <span className={styles.muted}>
                      {d.bookings} {d.bookings === 1 ? 'booking' : 'bookings'}
                    </span>
                  )}
                  {d.fridayNote && !holiday && (
                    <span className={styles.muted}>{d.fridayNote}</span>
                  )}
                </div>
              );
            })}
            {Array.from({ length: trail }, (_, i) => (
              <div key={`t${i}`} className={`${styles.day} ${styles.blank}`} />
            ))}
          </div>
          <div className={styles.legend}>
            {LEGEND.map(([k, label]) => (
              <span key={k}>
                <i
                  className={`${styles.swatch} ${styles[k]}`}
                  aria-hidden="true"
                />
                {label}
              </span>
            ))}
          </div>
        </>
      )}
      <AddEventDrawer open={adding} onClose={() => setAdding(false)} />
      <CalendarEventDialog
        event={viewing}
        canManage={canManage}
        onClose={() => setViewing(null)}
        onEdit={setChanging}
      />
      {changing && (
        <AddEventDrawer
          key={changing.id}
          open
          replaces={changing}
          onClose={() => setChanging(null)}
        />
      )}
    </Card>
  );
}
